import { z } from 'zod';

/**
 * The temporal annotation sidecar: one three-level timeline per episode.
 *
 * A vendor may put this file anywhere and call it anything — intake finds it by validating candidate
 * JSON against the schema below rather than by matching a path, because the path is the one part of
 * the contract that is not stable. The *schema* is: a file either satisfies it exactly or it is not
 * an annotations file, and `.strict()` is what makes that a real answer instead of a guess about a
 * document that happens to have an `episodes` key.
 *
 * Three levels, and each says something the others cannot:
 *
 *   L1  one caption per episode — what the operator was asked to do
 *   L2  disjoint action spans   — what they were doing at a given moment
 *   L3  body-part sub-actions   — which hand, doing what, inside that action
 *
 * Every timestamp is milliseconds from the start of its own episode, which is the same origin the
 * transcoded preview uses, so a position in one is a position in the other with no conversion.
 */

/** A body-part sub-action nested inside an L2 span. */
export const annotationLevel3Schema = z
  .object({
    start_ms: z.number().int().nonnegative(),
    end_ms: z.number().int().nonnegative(),
    /** `mm:ss.SSS`, carried verbatim. Derived from the ms fields, never read in preference to them. */
    start_time: z.string(),
    end_time: z.string(),
    body_part: z.string(),
    sub_action: z.string()
  })
  .strict();

/**
 * One action span.
 *
 * `confidence` is optional because it genuinely is: the reference delivery carries it on 602 of its
 * 680 spans. Requiring it would reject the whole file over a field nothing depends on, and dropping
 * `.strict()` to tolerate it would stop the schema being able to identify the file at all.
 *
 * `level3` is required but frequently empty — 154 of those 680 spans have no body-part detail — and
 * an empty list is the file saying so, which is why the UI renders nothing rather than a placeholder.
 */
export const annotationLevel2Schema = z
  .object({
    start_ms: z.number().int().nonnegative(),
    end_ms: z.number().int().nonnegative(),
    start_time: z.string(),
    end_time: z.string(),
    action: z.string(),
    confidence: z.number().optional(),
    level3: z.array(annotationLevel3Schema)
  })
  .strict();

export const annotationEpisodeSchema = z
  .object({
    episode_id: z.number().int().nonnegative(),
    level1: z
      .object({
        caption: z.string(),
        level2: z.array(annotationLevel2Schema)
      })
      .strict()
  })
  .strict();

export const annotationsFileSchema = z
  .object({
    schema_version: z.number().int().positive(),
    episodes: z.array(annotationEpisodeSchema).min(1)
  })
  .strict();

export type AnnotationLevel3 = z.infer<typeof annotationLevel3Schema>;
export type AnnotationLevel2 = z.infer<typeof annotationLevel2Schema>;
export type AnnotationEpisode = z.infer<typeof annotationEpisodeSchema>;
export type AnnotationsFile = z.infer<typeof annotationsFileSchema>;

/** What `dataset_episodes.annotation_level` may hold. `null` means the dataset has no annotations. */
export type AnnotationDepth = 'L1' | 'L2' | 'L3';

/**
 * How deep this episode's annotations actually go.
 *
 * Depth is per episode, not per dataset: a delivery may caption every episode and only segment some.
 * Reporting the dataset's best level against an episode that does not have it is the exact failure
 * this replaces.
 */
export function annotationDepth(episode: AnnotationEpisode): AnnotationDepth {
  if (episode.level1.level2.some(s => s.level3.length > 0)) return 'L3';
  if (episode.level1.level2.length > 0) return 'L2';
  return 'L1';
}

/**
 * The span covering `ms`, or null in a gap.
 *
 * Binary search rather than a scan because this runs on every `timeupdate` — four times a second
 * while playing, and once per pointer move while scrubbing — against up to a few hundred spans.
 * Spans are disjoint and ordered, so "the span at t" is a single unambiguous answer; the caller
 * sorts once and this stays a lookup.
 *
 * The interval is half-open: a span owns its start and yields its end to the next one, so the two
 * sides of a boundary never both claim the playhead.
 */
export function spanAt<T extends { start_ms: number; end_ms: number }>(spans: T[], ms: number): T | null {
  let lo = 0,
    hi = spans.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ms < spans[mid].start_ms) hi = mid - 1;
    else if (ms >= spans[mid].end_ms) lo = mid + 1;
    else return spans[mid];
  }
  return null;
}
